import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as S from './HoverEnlargePhoto.styled';

/** Same delay as /logs username photos. */
export const PHOTO_HOVER_ENLARGE_DELAY_MS = 600;
/** ActivityLogs / report face preview. */
export const ACTIVITY_LOGS_PHOTO_PREVIEW_SIZE = 112;
/** Guest ID / key-card preview. */
export const GUEST_PHOTO_PREVIEW_SIZE = 280;
const PHOTO_PREVIEW_GAP = 8;

type Props = {
  /** When empty, the thumb (initials / icon) still enlarges. */
  src?: string,
  previewSize?: number,
  previewShape?: 'circle' | 'rounded',
  delayMs?: number,
  previewTestId?: string,
  children: React.ReactNode,
};

/**
 * One hover-enlarge method for every face thumb (activityLogs, reports, guests, empty).
 * Portal sits above RightBar so the preview is never trapped behind a panel tint.
 */
const HoverEnlargePhoto = ({
  src,
  previewSize = ACTIVITY_LOGS_PHOTO_PREVIEW_SIZE,
  previewShape = 'circle',
  delayMs = PHOTO_HOVER_ENLARGE_DELAY_MS,
  previewTestId,
  children,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number, left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const circle = previewShape === 'circle';
  const photoSrc = String(src || '').trim();

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const updatePos = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let top = rect.top - previewSize - PHOTO_PREVIEW_GAP;
    if (top < PHOTO_PREVIEW_GAP) {
      top = rect.bottom + PHOTO_PREVIEW_GAP;
    }
    let left = rect.left + (rect.width / 2) - (previewSize / 2);
    left = Math.max(
      PHOTO_PREVIEW_GAP,
      Math.min(left, window.innerWidth - previewSize - PHOTO_PREVIEW_GAP),
    );
    setPos({ top, left });
  };

  const onEnter = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      updatePos();
      setOpen(true);
    }, delayMs);
  };

  const onLeave = () => {
    clearTimer();
    setOpen(false);
    setPos(null);
  };

  useEffect(() => () => clearTimer(), []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updatePos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, previewSize]);

  return (
    <>
      <S.Anchor
        ref={anchorRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {children}
      </S.Anchor>
      {open && pos
        ? createPortal(
          <S.PreviewPopup
            style={{ top: pos.top, left: pos.left }}
            $size={previewSize}
            $circle={circle}
            data-testid={previewTestId}
          >
            {photoSrc ? (
              <S.PreviewImg src={photoSrc} alt="" $circle={circle} />
            ) : (
              <S.PreviewFallback $circle={circle} $size={previewSize}>
                {children}
              </S.PreviewFallback>
            )}
          </S.PreviewPopup>,
          document.body,
        )
        : null}
    </>
  );
};

export default HoverEnlargePhoto;
